import { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDebouncedValue } from '@mantine/hooks';
import {
  Badge,
  Box,
  Center,
  Group,
  Loader,
  Paper,
  ScrollArea,
  Stack,
  Tabs,
  Text,
  Textarea,
  TextInput,
  UnstyledButton,
  rem,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IoCheckmark, IoClose, IoCreateOutline, IoDocumentTextOutline, IoOpenOutline, IoStar } from 'react-icons/io5';
import { Breadcrumbs } from '@components';
import { SellerApplicationStatusBadge } from '@repo/seller-application-status-badge';
import {
  SELLER_ME_QUERY_KEY,
  sellerOnboardingApi,
  type BankSuggestOption,
  type SellerAccountType,
  type SellerApplicationUpdate,
  type SellerProfileChangeField,
} from '@modules/seller-onboarding/api';
import { authApi } from '@modules/auth/api/auth.api';
import { sellerNeedsRevisionRegistrationWizard } from '@modules/seller-onboarding/moderation-revision';
import { formatPickupPhoneForInput } from '@modules/pickup-points/utils/formatPickupPhone';
import { Button, DoubleCheckModal, Modal, QuietIconButton, Tooltip } from '@shared/ui';
import { formatPhoneNumber } from '@shared/utils/format-phone';
import { devProxyS3Url } from '@shared/utils/dev-proxy-s3-url';
import {
  isStorageDocumentUrl,
  openSellerDocument,
} from '@modules/seller-onboarding/open-seller-document';
import { sellerPrivacyPolicyUrl } from '@shared/utils/seller-help-url';
import { fileLabelFromUrl } from '@shared/utils/file-label-from-url';
import '@shared/styles/page.css';
import './SellerProfilePage.css';
import { ZoomableImage } from './ZoomableImage';

function reviewCountLabel(count: number): string {
  const n = Math.abs(count) % 100;
  const n1 = n % 10;
  if (n > 10 && n < 20) return `${count} отзывов`;
  if (n1 > 1 && n1 < 5) return `${count} отзыва`;
  if (n1 === 1) return `${count} отзыв`;
  return `${count} отзывов`;
}

function SellerShopRating({
  rating,
  reviewCount,
}: {
  rating?: number | null;
  reviewCount?: number;
}) {
  if (rating == null) {
    return <span className="seller-shop-rating seller-shop-rating--empty">Нет рейтинга</span>;
  }

  const value = Number(rating).toLocaleString('ru-RU', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  const count = reviewCount ?? 0;

  return (
    <span className="seller-shop-rating" aria-label={`Рейтинг магазина ${value} из 5, ${reviewCountLabel(count)}`}>
      <span className="seller-shop-rating__value">{value}</span>
      <IoStar size={16} className="seller-shop-rating__star" aria-hidden />
      <span className="seller-shop-rating__count">{reviewCountLabel(count)}</span>
    </span>
  );
}

function toApiPhone(raw: string): string {
  const d = raw.replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('8')) return `+7${d.slice(1)}`;
  if (d.length === 10) return `+7${d}`;
  if (d.length === 11 && d.startsWith('7')) return `+${d}`;
  if (raw.trim().startsWith('+') && d.length > 0) return `+${d}`;
  return d ? `+${d}` : '';
}

const ACCOUNT_LABELS: Record<SellerAccountType, string> = {
  LEGAL_ENTITY: 'Юридическое лицо (ООО / АО)',
  INDIVIDUAL_ENTREPRENEUR: 'Индивидуальный предприниматель (ИП)',
  SELF_EMPLOYED: 'Самозанятый',
};

const PROFILE_TABS = [
  { value: 'application' as const, label: 'Заявка и магазин' },
  { value: 'requisites' as const, label: 'Реквизиты' },
  { value: 'login' as const, label: 'Вход' },
  { value: 'documents' as const, label: 'Документы' },
] as const;

type ProfileTabValue = (typeof PROFILE_TABS)[number]['value'];

/**
 * Реквизиты для выплат после отправки заявки меняются только через поддержку (бэкенд отклоняет правку) —
 * защита от подмены счёта при доступе посторонних к кабинету. Код редактирования оставлен на случай возврата.
 */
const BANK_DETAILS_SELF_EDIT_ENABLED = false;

const PROFILE_CHANGE_FIELD_LABELS: Record<SellerProfileChangeField, string> = {
  logo: 'Логотип',
  shopName: 'Название',
  activityDescription: 'Описание',
};

const EMAIL_ERR_EMPTY = 'Укажите email';
const EMAIL_ERR_FORMAT = 'Введите корректный email';
const PHONE_ERR_EMPTY = 'Укажите телефон';
const PHONE_ERR_FORMAT = 'Введите номер в формате +7…';
const SHOP_NAME_ERR_EMPTY = 'Укажите название магазина';
const ACTIVITY_ERR_EMPTY = 'Заполните описание деятельности';

function isValidEmailFormat(value: string): boolean {
  const s = value.trim();
  if (!s) return false;
  // Базовая проверка, согласованная с @IsEmail на бэке
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('ru-RU', {
      dateStyle: 'long',
    });
  } catch {
    return iso;
  }
}

function Row({
  label,
  value,
  unstyledValue,
}: {
  label: string;
  value: React.ReactNode;
  unstyledValue?: boolean;
}) {
  return (
    <div className="seller-profile-row">
      <Text size="sm" c="dimmed" className="seller-profile-row-label">
        {label}
      </Text>
      <Box className="seller-profile-row-value">
        {unstyledValue ? (
          value
        ) : (
          <Text size="sm" component="div">
            {value ?? '—'}
          </Text>
        )}
      </Box>
    </div>
  );
}

function ProfileCard({
  title,
  actions,
  children,
}: {
  title: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="seller-profile-card">
      <div className="seller-profile-card__head">
        <h3 className="seller-profile-card__title">{title}</h3>
        {actions ?? null}
      </div>
      <div className="seller-profile-card__body">{children}</div>
    </section>
  );
}

type LoginBindKind = 'phone' | 'email';

function LoginCredentialBindRow({
  kind,
  currentValue,
  canEdit,
  onBound,
}: {
  kind: LoginBindKind;
  currentValue: string | null | undefined;
  canEdit: boolean;
  onBound: () => void;
}) {
  const label = kind === 'phone' ? 'Телефон регистрации' : 'Email регистрации';
  const display =
    kind === 'phone'
      ? formatPhoneNumber(currentValue) || '—'
      : currentValue?.trim() || '—';
  const hasValue = Boolean(currentValue?.trim());
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setEditing(false);
    setDraft('');
    setCode('');
    setCodeSent(false);
    setError(null);
    setSending(false);
    setVerifying(false);
  };

  const startEdit = () => {
    setDraft(
      kind === 'phone'
        ? formatPickupPhoneForInput(currentValue ?? '')
        : (currentValue ?? ''),
    );
    setCode('');
    setCodeSent(false);
    setError(null);
    setEditing(true);
  };

  const sendCode = async () => {
    setError(null);
    if (kind === 'phone') {
      const phone = toApiPhone(draft);
      if (!/^\+7\d{10}$/.test(phone)) {
        setError('Введите номер в формате +7…');
        return;
      }
      setSending(true);
      try {
        await authApi.sendBindPhoneCode(phone);
        setCodeSent(true);
        setCode('');
        notifications.show({
          title: 'Код отправлен',
          message: 'Проверьте SMS',
          color: 'green',
        });
      } catch (e: unknown) {
        const msg =
          (e as { response?: { data?: { message?: string } } })?.response?.data
            ?.message || 'Не удалось отправить код';
        setError(msg);
      } finally {
        setSending(false);
      }
      return;
    }

    const email = draft.trim().toLowerCase();
    if (!isValidEmailFormat(email)) {
      setError('Введите корректный email');
      return;
    }
    setSending(true);
    try {
      await authApi.sendBindEmailCode(email);
      setCodeSent(true);
      setCode('');
      notifications.show({
        title: 'Код отправлен',
        message: 'Проверьте почту',
        color: 'green',
      });
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || 'Не удалось отправить код';
      setError(msg);
    } finally {
      setSending(false);
    }
  };

  const verify = async () => {
    setError(null);
    const codeTrim = code.replace(/\D/g, '');
    if (codeTrim.length < 4) {
      setError('Введите код из сообщения');
      return;
    }
    setVerifying(true);
    try {
      if (kind === 'phone') {
        await authApi.verifyBindPhone(toApiPhone(draft), codeTrim);
      } else {
        await authApi.verifyBindEmail(draft.trim().toLowerCase(), codeTrim);
      }
      notifications.show({
        title: 'Готово',
        message:
          kind === 'phone'
            ? 'Телефон для входа обновлён'
            : 'Email для входа обновлён',
        color: 'green',
      });
      reset();
      onBound();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || 'Неверный код';
      setError(msg);
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="seller-profile-row seller-profile-login-row">
      <Text size="sm" c="dimmed" className="seller-profile-row-label">
        {label}
      </Text>
      <Box className="seller-profile-row-value">
        {!editing ? (
          <div className="seller-profile-inline-edit">
            <Text size="sm" component="div">
              {display}
            </Text>
            {canEdit ? (
              <button
                type="button"
                className="seller-profile-login-action"
                onClick={startEdit}
              >
                {hasValue ? 'Изменить' : 'Добавить'}
              </button>
            ) : null}
          </div>
        ) : (
          <Stack gap="xs" className="seller-profile-login-edit">
            <TextInput
              size="sm"
              type={kind === 'phone' ? 'tel' : 'email'}
              inputMode={kind === 'phone' ? 'tel' : 'email'}
              placeholder={
                kind === 'phone' ? '+7 (900) 000-00-00' : 'email@example.com'
              }
              value={draft}
              onChange={(e) => {
                const v = e.currentTarget.value;
                setDraft(kind === 'phone' ? formatPickupPhoneForInput(v) : v);
                setError(null);
              }}
              disabled={sending || verifying || codeSent}
              error={error && !codeSent ? error : undefined}
            />
            {codeSent ? (
              <TextInput
                size="sm"
                label="Код подтверждения"
                placeholder="••••"
                inputMode="numeric"
                value={code}
                onChange={(e) => {
                  setCode(e.currentTarget.value.replace(/\D/g, '').slice(0, 6));
                  setError(null);
                }}
                disabled={verifying}
                error={error || undefined}
              />
            ) : null}
            <Group gap="xs">
              {!codeSent ? (
                <Button
                  variant="primary"
                  size="xs"
                  loading={sending}
                  onClick={() => void sendCode()}
                >
                  Получить код
                </Button>
              ) : (
                <Button
                  variant="primary"
                  size="xs"
                  loading={verifying}
                  onClick={() => void verify()}
                >
                  Подтвердить
                </Button>
              )}
              <button
                type="button"
                className="seller-profile-login-action"
                disabled={sending || verifying}
                onClick={reset}
              >
                Отмена
              </button>
            </Group>
            {codeSent ? (
              <UnstyledButton
                type="button"
                className="seller-profile-login-resend"
                disabled={sending || verifying}
                onClick={() => {
                  setCodeSent(false);
                  setCode('');
                  setError(null);
                }}
              >
                Изменить {kind === 'phone' ? 'номер' : 'email'}
              </UnstyledButton>
            ) : null}
          </Stack>
        )}
      </Box>
    </div>
  );
}

function DocTile({
  title,
  meta,
  href,
}: {
  title: string;
  meta?: string | null;
  href?: string | null;
}) {
  const url = href?.trim() ? href.trim() : null;
  // Паспорт, ЕГРЮЛ, оферта — из закрытого бакета по временной ссылке; страницы сайта — обычной ссылкой
  const storageDocument = isStorageDocumentUrl(url);
  if (!url) {
    return (
      <div className="seller-profile-doc-tile seller-profile-doc-tile--empty" aria-disabled>
        <span className="seller-profile-doc-tile__icon" aria-hidden>
          <IoDocumentTextOutline size={22} />
        </span>
        <div className="seller-profile-doc-tile__text">
          <span className="seller-profile-doc-tile__title">{title}</span>
          <span className="seller-profile-doc-tile__meta">Не загружено</span>
        </div>
      </div>
    );
  }
  return (
    <a
      className="seller-profile-doc-tile"
      href={storageDocument ? '#' : url}
      target={storageDocument ? undefined : '_blank'}
      rel="noopener noreferrer"
      onClick={
        storageDocument
          ? (event) => {
            event.preventDefault();
            openSellerDocument(url);
          }
          : undefined
      }
      title={meta ? `${title}: ${meta}` : title}
    >
      <span className="seller-profile-doc-tile__icon" aria-hidden>
        <IoDocumentTextOutline size={22} />
      </span>
      <div className="seller-profile-doc-tile__text">
        <span className="seller-profile-doc-tile__title">{title}</span>
        {meta ? <span className="seller-profile-doc-tile__meta">{meta}</span> : null}
      </div>
      <span className="seller-profile-doc-tile__open" aria-hidden>
        <IoOpenOutline size={16} />
      </span>
    </a>
  );
}

function DocTilesFromUrls({
  title,
  urls,
}: {
  title: string;
  urls: string[] | null | undefined;
}) {
  const list = urls?.filter((u) => u?.trim()) ?? [];
  if (!list.length) {
    return <DocTile title={title} />;
  }
  return (
    <>
      {list.map((url, index) => (
        <DocTile
          key={url}
          title={list.length > 1 ? `${title} · ${index + 1}` : title}
          meta={fileLabelFromUrl(url)}
          href={url}
        />
      ))}
    </>
  );
}

export default function SellerProfilePage() {
  const navigate = useNavigate();
  const [activeProfileTab, setActiveProfileTab] = useState<ProfileTabValue>('application');
  const [emailEditing, setEmailEditing] = useState(false);
  const [emailDraft, setEmailDraft] = useState('');
  const [emailFieldError, setEmailFieldError] = useState<string | null>(null);
  const [phoneEditing, setPhoneEditing] = useState(false);
  const [phoneDraft, setPhoneDraft] = useState('');
  const [phoneFieldError, setPhoneFieldError] = useState<string | null>(null);
  const [shopNameEditing, setShopNameEditing] = useState(false);
  const [shopNameDraft, setShopNameDraft] = useState('');
  const [shopNameFieldError, setShopNameFieldError] = useState<string | null>(null);
  const [activityEditing, setActivityEditing] = useState(false);
  const [activityDraft, setActivityDraft] = useState('');
  const [activityFieldError, setActivityFieldError] = useState<string | null>(null);
  const logoFileInputId = useId();
  const [deleteProfileModalOpen, setDeleteProfileModalOpen] = useState(false);
  const [hideProfileModalOpen, setHideProfileModalOpen] = useState(false);
  const [cancelProfileChangeField, setCancelProfileChangeField] =
    useState<SellerProfileChangeField | null>(null);
  /** Что только что ушло на модерацию — для модалки-пояснения после сохранения. */
  const [sentToModerationLabel, setSentToModerationLabel] = useState<string | null>(null);
  const [bankEditing, setBankEditing] = useState(false);
  const [bankDraft, setBankDraft] = useState({
    bik: '',
    bankName: '',
    bankAccountNumber: '',
    correspondentAccount: '',
  });
  const [bankSearchQuery, setBankSearchQuery] = useState('');
  const [debouncedBankQuery] = useDebouncedValue(bankSearchQuery, 380);
  const [bankSuggestions, setBankSuggestions] = useState<BankSuggestOption[]>([]);
  const [bankSuggestLoading, setBankSuggestLoading] = useState(false);
  const allowBankSuggestFetch = useRef(false);

  const resetInlineEditsExcept = (
    keep: 'email' | 'phone' | 'shop' | 'activity' | 'bank',
  ) => {
    if (keep !== 'email') {
      setEmailEditing(false);
      setEmailFieldError(null);
    }
    if (keep !== 'phone') {
      setPhoneEditing(false);
      setPhoneFieldError(null);
    }
    if (keep !== 'shop') {
      setShopNameEditing(false);
      setShopNameFieldError(null);
    }
    if (keep !== 'activity') {
      setActivityEditing(false);
      setActivityFieldError(null);
    }
    if (keep !== 'bank') {
      setBankEditing(false);
      setBankSuggestions([]);
      allowBankSuggestFetch.current = false;
    }
  };

  const queryClient = useQueryClient();
  const { data: app, isLoading, error } = useQuery({
    queryKey: SELLER_ME_QUERY_KEY,
    queryFn: () => sellerOnboardingApi.getMe(),
  });
  const [offerHistoryOpen, setOfferHistoryOpen] = useState(false);
  const { data: offerDocuments } = useQuery({
    queryKey: ['seller-offer-documents'],
    queryFn: () => sellerOnboardingApi.listOfferDocuments(),
    enabled: Boolean(app?.offerPdfFileUrl?.trim()),
  });
  const currentOfferPdfUrl = app?.offerPdfFileUrl?.trim() ?? '';
  const currentOfferTemplateVersion = app?.offerTemplateVersion ?? null;
  const previousOfferDocuments = (offerDocuments ?? []).filter((doc) => {
    if (doc.pdfFileUrl.trim() === currentOfferPdfUrl) return false;
    if (
      currentOfferTemplateVersion != null &&
      doc.templateVersion === currentOfferTemplateVersion &&
      !doc.acceptedAt
    ) {
      return false;
    }
    return true;
  });

  useEffect(() => {
    if (!bankEditing) {
      setBankSuggestions([]);
      allowBankSuggestFetch.current = false;
      return;
    }
    const q = debouncedBankQuery.trim();
    if (q.length < 2) {
      setBankSuggestions([]);
      return;
    }
    if (!allowBankSuggestFetch.current) {
      setBankSuggestions([]);
      return;
    }
    let cancelled = false;
    setBankSuggestLoading(true);
    sellerOnboardingApi
      .suggestBank({ query: q })
      .then((r) => {
        if (cancelled || !allowBankSuggestFetch.current) return;
        setBankSuggestions(r.suggestions);
      })
      .catch(() => {
        if (!cancelled && allowBankSuggestFetch.current) setBankSuggestions([]);
      })
      .finally(() => {
        if (!cancelled) setBankSuggestLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedBankQuery, bankEditing]);

  const showSaveError = (err: unknown) => {
    const msg = String(
      (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
      (err as Error)?.message ??
      '',
    );
    notifications.show({
      title: 'Не удалось сохранить',
      message: msg || 'Попробуйте снова.',
      color: 'red',
    });
  };

  const updateSection = useMutation({
    mutationFn: (body: SellerApplicationUpdate) => sellerOnboardingApi.update(body),
    onSuccess: (data, variables) => {
      void queryClient.invalidateQueries({ queryKey: SELLER_ME_QUERY_KEY });
      const keys = Object.keys(variables) as (keyof SellerApplicationUpdate)[];
      if (keys.includes('shopName') && data.pendingShopName != null) {
        setSentToModerationLabel('Новое название магазина');
      } else if (keys.includes('activityDescription') && data.pendingActivityDescription != null) {
        setSentToModerationLabel('Новое описание магазина');
      } else {
        notifications.show({
          title: 'Сохранено',
          message: 'Данные обновлены.',
          color: 'green',
        });
      }
      if (keys.includes('contactEmail')) {
        setEmailEditing(false);
      }
      if (keys.includes('contactPhone')) {
        setPhoneEditing(false);
      }
      if (keys.includes('shopName')) {
        setShopNameEditing(false);
      }
      if (keys.includes('activityDescription')) {
        setActivityEditing(false);
      }
      if (
        keys.some(
          (k) =>
            k === 'bik' ||
            k === 'bankName' ||
            k === 'bankAccountNumber' ||
            k === 'correspondentAccount',
        )
      ) {
        setBankEditing(false);
      }
    },
    onError: showSaveError,
  });

  const profileVisibilityMutation = useMutation({
    mutationFn: (hidden: boolean) =>
      hidden ? sellerOnboardingApi.hideProfile() : sellerOnboardingApi.showProfile(),
    onSuccess: (_data, hidden) => {
      setHideProfileModalOpen(false);
      void queryClient.invalidateQueries({ queryKey: SELLER_ME_QUERY_KEY });
      notifications.show({
        title: hidden ? 'Магазин скрыт' : 'Магазин показан',
        message: hidden
          ? 'Товары и профиль магазина не видны покупателям. Кабинет работает как обычно.'
          : 'Магазин снова виден на витрине.',
        color: 'green',
      });
    },
    onError: showSaveError,
  });

  const requestProfileDeletionMutation = useMutation({
    mutationFn: () => sellerOnboardingApi.requestProfileDeletion(),
    onSuccess: () => {
      setDeleteProfileModalOpen(false);
      void queryClient.invalidateQueries({ queryKey: SELLER_ME_QUERY_KEY });
      notifications.show({
        title: 'Заявка принята',
        message:
          'Магазин скрыт с витрины. Удаление произойдёт через 30 дней, если вы не отмените заявку.',
        color: 'green',
      });
    },
    onError: showSaveError,
  });

  const cancelProfileDeletionMutation = useMutation({
    mutationFn: () => sellerOnboardingApi.cancelProfileDeletion(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SELLER_ME_QUERY_KEY });
      notifications.show({
        title: 'Заявка отменена',
        message: 'Магазин снова показан на витрине.',
        color: 'green',
      });
    },
    onError: showSaveError,
  });

  const uploadLogoMutation = useMutation({
    mutationFn: (file: File) => sellerOnboardingApi.uploadFile('logo', file),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: SELLER_ME_QUERY_KEY });
      if (data.pendingLogoFileUrl) {
        setSentToModerationLabel('Новый логотип');
        return;
      }
      notifications.show({
        title: 'Сохранено',
        message: 'Логотип обновлён.',
        color: 'green',
      });
    },
    onError: showSaveError,
  });

  const dismissRejectionMutation = useMutation({
    mutationFn: () => sellerOnboardingApi.dismissProfileChangesRejection(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SELLER_ME_QUERY_KEY });
    },
    onError: showSaveError,
  });

  const cancelProfileChangesMutation = useMutation({
    mutationFn: (field: SellerProfileChangeField) =>
      sellerOnboardingApi.cancelProfileChanges(field),
    onSuccess: (_data, field) => {
      setCancelProfileChangeField(null);
      void queryClient.invalidateQueries({ queryKey: SELLER_ME_QUERY_KEY });
      notifications.show({
        title: `${PROFILE_CHANGE_FIELD_LABELS[field]}: изменение отозвано`,
        message: 'На витрине остаётся текущее значение.',
        color: 'green',
      });
    },
    onError: showSaveError,
  });

  const startEmailEdit = () => {
    if (!app) return;
    resetInlineEditsExcept('email');
    setEmailDraft(app.contactEmail ?? '');
    setEmailFieldError(null);
    setEmailEditing(true);
  };

  const startPhoneEdit = () => {
    if (!app) return;
    resetInlineEditsExcept('phone');
    setPhoneDraft(formatPickupPhoneForInput(app.contactPhone ?? ''));
    setPhoneFieldError(null);
    setPhoneEditing(true);
  };

  const cancelPhoneEdit = () => {
    setPhoneFieldError(null);
    setPhoneEditing(false);
  };

  const savePhone = () => {
    const normalized = toApiPhone(phoneDraft);
    const digits = normalized.replace(/\D/g, '');
    if (!digits) {
      setPhoneFieldError(PHONE_ERR_EMPTY);
      return;
    }
    if (digits.length !== 11 || !digits.startsWith('7')) {
      setPhoneFieldError(PHONE_ERR_FORMAT);
      return;
    }
    setPhoneFieldError(null);
    updateSection.mutate({ contactPhone: normalized });
  };

  const startShopNameEdit = () => {
    if (!app) return;
    resetInlineEditsExcept('shop');
    setShopNameDraft(app.pendingShopName ?? app.seller?.shopName ?? app.shopName ?? '');
    setShopNameFieldError(null);
    setShopNameEditing(true);
  };

  const cancelShopNameEdit = () => {
    setShopNameFieldError(null);
    setShopNameEditing(false);
  };

  const saveShopName = () => {
    const trimmed = shopNameDraft.trim();
    if (!trimmed) {
      setShopNameFieldError(SHOP_NAME_ERR_EMPTY);
      return;
    }
    setShopNameFieldError(null);
    updateSection.mutate({ shopName: trimmed });
  };

  const startActivityEdit = () => {
    if (!app) return;
    resetInlineEditsExcept('activity');
    setActivityDraft(app.pendingActivityDescription ?? app.activityDescription ?? '');
    setActivityFieldError(null);
    setActivityEditing(true);
  };

  const cancelActivityEdit = () => {
    setActivityFieldError(null);
    setActivityEditing(false);
  };

  const saveActivity = () => {
    const trimmed = activityDraft.trim();
    if (!trimmed) {
      setActivityFieldError(ACTIVITY_ERR_EMPTY);
      return;
    }
    setActivityFieldError(null);
    updateSection.mutate({ activityDescription: trimmed });
  };

  const beginBankEdit = () => {
    if (!app) return;
    resetInlineEditsExcept('bank');
    allowBankSuggestFetch.current = false;
    setBankSuggestions([]);
    setBankDraft({
      bik: app.bik ?? '',
      bankName: app.bankName ?? '',
      bankAccountNumber: app.bankAccountNumber ?? '',
      correspondentAccount: app.correspondentAccount ?? '',
    });
    setBankSearchQuery(app.bankName?.trim() ?? '');
    setBankEditing(true);
  };

  const cancelBankEdit = () => {
    setBankEditing(false);
    setBankSuggestions([]);
    allowBankSuggestFetch.current = false;
  };

  const handleBankSuggestionPick = (opt: BankSuggestOption) => {
    allowBankSuggestFetch.current = false;
    setBankDraft((d) => ({
      ...d,
      bik: opt.bik,
      bankName: opt.bankName,
      correspondentAccount: opt.correspondentAccount?.trim().length
        ? opt.correspondentAccount.trim()
        : '',
    }));
    setBankSearchQuery((opt.bankName || opt.label).trim());
    setBankSuggestions([]);
  };

  const saveBank = () => {
    updateSection.mutate({
      bik: bankDraft.bik.trim(),
      bankName: bankDraft.bankName.trim(),
      bankAccountNumber: bankDraft.bankAccountNumber.trim(),
      correspondentAccount: bankDraft.correspondentAccount.trim(),
    });
  };

  const cancelEmailEdit = () => {
    setEmailFieldError(null);
    setEmailEditing(false);
  };

  const saveEmail = () => {
    const trimmed = emailDraft.trim();
    if (!trimmed) {
      setEmailFieldError(EMAIL_ERR_EMPTY);
      return;
    }
    if (!isValidEmailFormat(trimmed)) {
      setEmailFieldError(EMAIL_ERR_FORMAT);
      return;
    }
    setEmailFieldError(null);
    updateSection.mutate({ contactEmail: trimmed });
  };

  if (isLoading || !app) {
    return (
      <div className="page-container seller-profile-page">
        <Breadcrumbs
          items={[
            { label: 'Главная', path: '/' },
            { label: 'Мои данные' },
          ]}
        />
        <div className="page-header">
          <div className="page-header-top">
            <h1>Мои данные</h1>
          </div>
        </div>
        <div className="content-wrapper">
          <Center mih={280}>
            <Loader />
          </Center>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container seller-profile-page">
        <Breadcrumbs
          items={[
            { label: 'Главная', path: '/' },
            { label: 'Мои данные' },
          ]}
        />
        <div className="page-header">
          <div className="page-header-top">
            <h1>Мои данные</h1>
          </div>
        </div>
        <div className="content-wrapper">
          <Text c="red">Не удалось загрузить данные. Попробуйте обновить страницу.</Text>
        </div>
      </div>
    );
  }

  const canManageProfileVisibility =
    app.status === 'APPROVED' &&
    app.seller != null &&
    app.cabinetRole !== 'STAFF' &&
    !app.seller.accountBlock &&
    !app.seller.profileDeletion?.deletedAt;
  const rejectedProfileChanges = (
    Object.keys(PROFILE_CHANGE_FIELD_LABELS) as SellerProfileChangeField[]
  ).flatMap((field) => {
    const entry = app.profileChangesRejected?.[field];
    return entry ? [{ field, value: entry.value, reason: entry.reason }] : [];
  });
  const pendingProfileChanges = (
    Object.keys(PROFILE_CHANGE_FIELD_LABELS) as SellerProfileChangeField[]
  ).flatMap((field) => {
    const value =
      field === 'logo'
        ? app.pendingLogoFileUrl
        : field === 'shopName'
          ? app.pendingShopName
          : app.pendingActivityDescription;
    return value != null ? [{ field, value }] : [];
  });
  const hasPendingProfileChanges = pendingProfileChanges.length > 0;
  const isProfileHidden = Boolean(app.seller?.profileHidden);
  const profileDeletion = app.seller?.profileDeletion;
  const isProfileDeletionPending =
    Boolean(profileDeletion) && !profileDeletion?.deletedAt;
  const isProfileDeleted = Boolean(profileDeletion?.deletedAt);
  const profileDeletionScheduledLabel = profileDeletion?.scheduledAt
    ? formatDate(profileDeletion.scheduledAt)
    : null;

  const statusBadge = app.seller?.accountBlock ? (
    <Badge color="red" variant="light">
      Заблокирован
    </Badge>
  ) : isProfileDeleted ? (
    <Badge color="red" variant="light">
      Удалён
    </Badge>
  ) : isProfileDeletionPending ? (
    <Badge color="orange" variant="light">
      Удаление запланировано
    </Badge>
  ) : isProfileHidden ? (
    <Badge color="gray" variant="light">
      Скрыт с витрины
    </Badge>
  ) : (
    <SellerApplicationStatusBadge status={app.status} />
  );

  const bankCardActions = bankEditing ? (
    <div className="seller-profile-edit-actions">
      <QuietIconButton
        icon={<IoCheckmark size={18} />}
        tone="accent"
        onClick={saveBank}
        loading={updateSection.isPending}
        aria-label="Сохранить"
      />
      <QuietIconButton
        icon={<IoClose size={18} />}
        tone="danger"
        onClick={cancelBankEdit}
        disabled={updateSection.isPending}
        aria-label="Отменить"
      />
    </div>
  ) : (
    <QuietIconButton
      icon={<IoCreateOutline size={18} />}
      onClick={beginBankEdit}
      aria-label="Редактировать: Банковские данные"
    />
  );

  return (
    <div className="page-container seller-profile-page">
      <Breadcrumbs
        items={[
          { label: 'Главная', path: '/' },
          { label: 'Мои данные' },
        ]}
      />

      <Tabs
        value={activeProfileTab}
        onChange={(v) => v && setActiveProfileTab(v as ProfileTabValue)}
        className="seller-profile-tabs"
      >
        <div className="page-header">
          <div className="page-header-top">
            <h1>Мои данные</h1>
          </div>
          <Tabs.List className="seller-profile-tabs-list">
            {PROFILE_TABS.map((tab) => (
              <Tabs.Tab
                key={tab.value}
                value={tab.value}
                className="seller-profile-tabs-tab"
              >
                {tab.label}
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </div>

        <div className="content-wrapper">
          <div className="seller-profile-page__inner">
            {sellerNeedsRevisionRegistrationWizard(
              app.status,
              app.moderationRejectionReason,
              app.moderationRejectionKind,
            ) ? (
              <div className="seller-profile-revision-callout">
                <p className="seller-profile-revision-callout__eyebrow">Заявка отклонена</p>
                <h2 className="seller-profile-revision-callout__title">
                  Нужно обновить данные заявки
                </h2>
                <p className="seller-profile-revision-callout__text">
                  Откройте анкету регистрации — можно заменить нужные данные и
                  отправить заявку снова.
                </p>
                <Button
                  type="button"
                  variant="primary"
                  className="seller-profile-revision-callout__cta"
                  onClick={() => navigate('/registration')}
                >
                  Изменить данные
                </Button>
              </div>
            ) : null}

            <Tabs.Panel value="application" className="seller-profile-tab-panel">
              <section className="seller-profile-card">
                <div className="seller-profile-identity">
                  <input
                    id={logoFileInputId}
                    type="file"
                    accept="image/*"
                    style={{
                      position: 'absolute',
                      width: 1,
                      height: 1,
                      padding: 0,
                      margin: -1,
                      overflow: 'hidden',
                      clip: 'rect(0, 0, 0, 0)',
                      whiteSpace: 'nowrap',
                      border: 0,
                      opacity: 0,
                    }}
                    tabIndex={-1}
                    disabled={uploadLogoMutation.isPending || updateSection.isPending}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      e.currentTarget.value = '';
                      if (file) uploadLogoMutation.mutate(file);
                    }}
                  />
                  <label
                    htmlFor={logoFileInputId}
                    className={`seller-profile-logo-wrap${uploadLogoMutation.isPending ? ' seller-profile-logo-wrap--busy' : ''}${updateSection.isPending ? ' seller-profile-logo-wrap--disabled' : ''}`}
                    aria-label="Выбрать изображение логотипа"
                    title="Квадратный логотип 1:1, желательно 512×512 px (PNG или JPEG до 2 МБ)"
                  >
                    {app.logoFileUrl?.trim() ? (
                      <img
                        src={devProxyS3Url(app.logoFileUrl) || app.logoFileUrl}
                        alt="Логотип магазина"
                        className="seller-profile-logo-preview"
                      />
                    ) : (
                      <Box className="seller-profile-logo-preview seller-profile-logo-placeholder">
                        Логотип
                      </Box>
                    )}
                    {!uploadLogoMutation.isPending && !updateSection.isPending ? (
                      <div className="seller-profile-logo-hover-overlay" aria-hidden>
                        <IoCreateOutline size={28} className="seller-profile-logo-hover-icon" />
                      </div>
                    ) : null}
                    {uploadLogoMutation.isPending ? (
                      <div className="seller-profile-logo-loading-overlay">
                        <Loader size={24} />
                      </div>
                    ) : null}
                  </label>

                  <div className="seller-profile-identity__main">
                    <div
                      className={`seller-profile-identity__name-row${shopNameEditing ? ' seller-profile-identity__name-row--editing' : ''}`}
                    >
                      {shopNameEditing ? (
                        <TextInput
                          className="seller-profile-inline-input seller-profile-name-input"
                          size="sm"
                          value={shopNameDraft}
                          onChange={(e) => {
                            setShopNameDraft(e.target.value);
                            setShopNameFieldError(null);
                          }}
                          error={shopNameFieldError}
                          disabled={updateSection.isPending}
                        />
                      ) : (
                        <h2 className="seller-profile-identity__name">
                          {app.seller?.shopName ?? app.shopName ?? '—'}
                        </h2>
                      )}
                      {!shopNameEditing ? (
                        <QuietIconButton
                          icon={<IoCreateOutline size={18} />}
                          onClick={startShopNameEdit}
                          aria-label="Редактировать название магазина"
                        />
                      ) : (
                        <div className="seller-profile-edit-actions">
                          <QuietIconButton
                            icon={<IoCheckmark size={18} />}
                            tone="accent"
                            onClick={saveShopName}
                            loading={updateSection.isPending}
                            aria-label="Сохранить"
                          />
                          <QuietIconButton
                            icon={<IoClose size={18} />}
                            tone="danger"
                            onClick={cancelShopNameEdit}
                            disabled={updateSection.isPending}
                            aria-label="Отменить"
                          />
                        </div>
                      )}
                    </div>

                    <div className="seller-profile-identity__status">{statusBadge}</div>

                    <p className="seller-profile-identity__meta">
                      Заявка #{app.id} · {formatDate(app.createdAt)}
                    </p>
                    {app.sellerId != null ? (
                      <SellerShopRating
                        rating={app.averageRating}
                        reviewCount={app.approvedReviewCount}
                      />
                    ) : null}

                    <div
                      className={`seller-profile-identity__activity-row${activityEditing ? ' seller-profile-identity__activity-row--editing' : ''}`}
                    >
                      {activityEditing ? (
                        <Textarea
                          className="seller-profile-activity-inline"
                          size="sm"
                          minRows={3}
                          autosize
                          maxRows={12}
                          value={activityDraft}
                          onChange={(e) => {
                            setActivityDraft(e.target.value);
                            setActivityFieldError(null);
                          }}
                          error={activityFieldError}
                          disabled={updateSection.isPending}
                        />
                      ) : (
                        <p className="seller-profile-identity__activity">
                          {app.activityDescription?.trim() || 'Описание деятельности не указано'}
                        </p>
                      )}
                      {!activityEditing ? (
                        <QuietIconButton
                          icon={<IoCreateOutline size={18} />}
                          onClick={startActivityEdit}
                          aria-label="Редактировать описание деятельности"
                        />
                      ) : (
                        <div className="seller-profile-edit-actions">
                          <QuietIconButton
                            icon={<IoCheckmark size={18} />}
                            tone="accent"
                            onClick={saveActivity}
                            loading={updateSection.isPending}
                            aria-label="Сохранить"
                          />
                          <QuietIconButton
                            icon={<IoClose size={18} />}
                            tone="danger"
                            onClick={cancelActivityEdit}
                            disabled={updateSection.isPending}
                            aria-label="Отменить"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </section>

              {hasPendingProfileChanges ? (
                <section className="seller-profile-pending">
                  <div className="seller-profile-pending__head">
                    <span className="seller-profile-pending__dot" aria-hidden />
                    <h3 className="seller-profile-pending__title">Изменения на модерации</h3>
                  </div>
                  <p className="seller-profile-pending__hint">
                    Покупатели пока видят прежние данные
                    {app.profileChangesSubmittedAt
                      ? ` · отправлено ${formatDate(app.profileChangesSubmittedAt)}`
                      : ''}
                  </p>
                  <ul className="seller-profile-pending__list">
                    {pendingProfileChanges.map(({ field, value }) => (
                      <li
                        key={field}
                        className="seller-profile-pending__item seller-profile-pending__item--with-action"
                      >
                        <span className="seller-profile-pending__label">
                          {PROFILE_CHANGE_FIELD_LABELS[field]}
                        </span>
                        {field === 'logo' ? (
                          <ZoomableImage
                            src={devProxyS3Url(value) || value}
                            alt="Новый логотип на модерации"
                            className="seller-profile-pending__logo"
                          />
                        ) : (
                          <span
                            className={`seller-profile-pending__value${field === 'shopName' ? ' seller-profile-pending__value--name' : ''}`}
                          >
                            {value.trim() || '—'}
                          </span>
                        )}
                        <Tooltip label="Отозвать">
                          <QuietIconButton
                            icon={<IoClose size={16} />}
                            tone="danger"
                            disabled={cancelProfileChangesMutation.isPending}
                            onClick={() => setCancelProfileChangeField(field)}
                            aria-label={`Отозвать: ${PROFILE_CHANGE_FIELD_LABELS[field]}`}
                          />
                        </Tooltip>
                      </li>
                    ))}
                  </ul>
                </section>
              ) : null}

              {rejectedProfileChanges.length > 0 ? (
                <section className="seller-profile-pending seller-profile-pending--rejected">
                  <div className="seller-profile-pending__head">
                    <span className="seller-profile-pending__dot" aria-hidden />
                    <h3 className="seller-profile-pending__title">Изменения отклонены</h3>
                    {app.profileChangesRejectedAt ? (
                      <span className="seller-profile-pending__date">
                        {formatDate(app.profileChangesRejectedAt)}
                      </span>
                    ) : null}
                    <QuietIconButton
                      icon={<IoClose size={16} />}
                      className="seller-profile-pending__dismiss"
                      onClick={() => dismissRejectionMutation.mutate()}
                      loading={dismissRejectionMutation.isPending}
                      aria-label="Скрыть"
                    />
                  </div>
                  <p className="seller-profile-pending__hint">
                    Исправьте данные и сохраните снова — они повторно уйдут на модерацию.
                  </p>
                  <ul className="seller-profile-pending__list">
                    {rejectedProfileChanges.map(({ field, value, reason }) => (
                      <li key={field} className="seller-profile-pending__item">
                        <span className="seller-profile-pending__label">
                          {PROFILE_CHANGE_FIELD_LABELS[field]}
                        </span>
                        <div className="seller-profile-pending__rejected-body">
                          {field === 'logo' ? (
                            value ? (
                              <ZoomableImage
                                src={devProxyS3Url(value) || value}
                                alt="Отклонённый логотип"
                                className="seller-profile-pending__logo"
                              />
                            ) : null
                          ) : (
                            <span
                              className={`seller-profile-pending__value${field === 'shopName' ? ' seller-profile-pending__value--name' : ''}`}
                            >
                              {value?.trim() || '—'}
                            </span>
                          )}
                          <span className="seller-profile-pending__reason">{reason}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>
              ) : null}

              {canManageProfileVisibility ? (
                <div className="seller-profile-danger">
                  <h3 className="seller-profile-danger__title">Витрина магазина</h3>
                  {isProfileDeletionPending ? (
                    <>
                      <p className="seller-profile-danger__text">
                        Магазин скрыт с витрины. Если не отмените заявку, он будет удалён
                        {profileDeletionScheduledLabel
                          ? ` ${profileDeletionScheduledLabel}`
                          : ' через 30 дней'}
                        .
                      </p>
                      <div className="seller-profile-danger__actions">
                        <Badge color="orange" variant="light">
                          Удаление {profileDeletionScheduledLabel ?? 'через 30 дней'}
                        </Badge>
                        <UnstyledButton
                          type="button"
                          className="seller-profile-subtle-link"
                          disabled={cancelProfileDeletionMutation.isPending}
                          onClick={() => cancelProfileDeletionMutation.mutate()}
                        >
                          {cancelProfileDeletionMutation.isPending
                            ? 'Отмена…'
                            : 'Отменить удаление'}
                        </UnstyledButton>
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="seller-profile-danger__text">
                        Сейчас магазин {isProfileHidden ? 'скрыт' : 'показан'} на витрине. При
                        скрытии покупатели не увидят товары, точки и страницу магазина — кабинет
                        останется доступным.
                      </p>
                      <div className="seller-profile-danger__actions">
                        <Badge color={isProfileHidden ? 'gray' : 'green'} variant="light">
                          {isProfileHidden ? 'Скрыт' : 'Показан'}
                        </Badge>
                        <UnstyledButton
                          type="button"
                          className="seller-profile-subtle-link"
                          disabled={profileVisibilityMutation.isPending}
                          onClick={() => {
                            if (isProfileHidden) {
                              profileVisibilityMutation.mutate(false);
                            } else {
                              setHideProfileModalOpen(true);
                            }
                          }}
                        >
                          {profileVisibilityMutation.isPending
                            ? 'Сохранение…'
                            : isProfileHidden
                              ? 'Показать на витрине'
                              : 'Скрыть с витрины'}
                        </UnstyledButton>
                        <UnstyledButton
                          type="button"
                          className="seller-profile-subtle-link seller-profile-subtle-link--danger"
                          onClick={() => setDeleteProfileModalOpen(true)}
                        >
                          Удалить магазин
                        </UnstyledButton>
                      </div>
                    </>
                  )}
                </div>
              ) : null}
            </Tabs.Panel>

            <Tabs.Panel value="requisites" className="seller-profile-tab-panel">
              <ProfileCard title="Организация">
                <Row
                  label="Тип организации"
                  value={app.accountType ? ACCOUNT_LABELS[app.accountType] : '—'}
                />
                <Row label="ИНН" value={app.inn} />
                {app.accountType === 'LEGAL_ENTITY' ? (
                  <Row label="КПП" value={app.kpp?.trim() || '—'} />
                ) : null}
                <Row label="ОГРН" value={app.ogrn} />
                <Row label="Наименование" value={app.companyName} />
                <Row label="Юридический адрес" value={app.legalAddress} />
                <Row label="ФИО руководителя" value={app.directorFullName} />
              </ProfileCard>

              <ProfileCard title="Контакты">
                <div className="seller-profile-row">
                  <Text size="sm" c="dimmed" className="seller-profile-row-label">
                    Телефон директора
                  </Text>
                  <Box className="seller-profile-row-value">
                    <div
                      className={`seller-profile-inline-edit${phoneEditing ? ' seller-profile-inline-edit--editing' : ''}`}
                    >
                      {phoneEditing ? (
                        <TextInput
                          className="seller-profile-inline-input"
                          size="sm"
                          type="tel"
                          inputMode="tel"
                          autoComplete="tel"
                          placeholder="+7 (900) 000-00-00"
                          value={phoneDraft}
                          onChange={(e) => {
                            setPhoneDraft(formatPickupPhoneForInput(e.currentTarget.value));
                            setPhoneFieldError(null);
                          }}
                          error={phoneFieldError}
                          disabled={updateSection.isPending}
                        />
                      ) : (
                        <Text size="sm" component="div">
                          {formatPhoneNumber(app.contactPhone) || '—'}
                        </Text>
                      )}
                      {!phoneEditing ? (
                        <QuietIconButton
                          icon={<IoCreateOutline size={18} />}
                          onClick={startPhoneEdit}
                          aria-label="Редактировать телефон директора"
                        />
                      ) : (
                        <div className="seller-profile-edit-actions">
                          <QuietIconButton
                            icon={<IoCheckmark size={18} />}
                            tone="accent"
                            onClick={savePhone}
                            loading={updateSection.isPending}
                            aria-label="Сохранить"
                          />
                          <QuietIconButton
                            icon={<IoClose size={18} />}
                            tone="danger"
                            onClick={cancelPhoneEdit}
                            disabled={updateSection.isPending}
                            aria-label="Отменить"
                          />
                        </div>
                      )}
                    </div>
                  </Box>
                </div>
                <div className="seller-profile-row">
                  <Text size="sm" c="dimmed" className="seller-profile-row-label">
                    Email директора
                  </Text>
                  <Box className="seller-profile-row-value">
                    <div
                      className={`seller-profile-inline-edit${emailEditing ? ' seller-profile-inline-edit--editing' : ''}`}
                    >
                      {emailEditing ? (
                        <TextInput
                          className="seller-profile-inline-input"
                          size="sm"
                          type="email"
                          autoComplete="email"
                          value={emailDraft}
                          onChange={(e) => {
                            setEmailDraft(e.target.value);
                            setEmailFieldError(null);
                          }}
                          error={emailFieldError}
                          disabled={updateSection.isPending}
                        />
                      ) : (
                        <Text size="sm" component="div">
                          {app.contactEmail ?? '—'}
                        </Text>
                      )}
                      {!emailEditing ? (
                        <QuietIconButton
                          icon={<IoCreateOutline size={18} />}
                          onClick={startEmailEdit}
                          aria-label="Редактировать email директора"
                        />
                      ) : (
                        <div className="seller-profile-edit-actions">
                          <QuietIconButton
                            icon={<IoCheckmark size={18} />}
                            tone="accent"
                            onClick={saveEmail}
                            loading={updateSection.isPending}
                            aria-label="Сохранить"
                          />
                          <QuietIconButton
                            icon={<IoClose size={18} />}
                            tone="danger"
                            onClick={cancelEmailEdit}
                            disabled={updateSection.isPending}
                            aria-label="Отменить"
                          />
                        </div>
                      )}
                    </div>
                  </Box>
                </div>
              </ProfileCard>

              <ProfileCard
                title="Банковские данные"
                actions={BANK_DETAILS_SELF_EDIT_ENABLED ? bankCardActions : undefined}
              >
                {bankEditing ? (
                  <>
                    <div className="seller-profile-row">
                      <Text size="sm" c="dimmed" className="seller-profile-row-label">
                        Название или БИК для поиска
                      </Text>
                      <Box className="seller-profile-row-value">
                        <Stack gap="xs" className="seller-profile-bank-search-block">
                          <TextInput
                            className="seller-profile-inline-input"
                            size="sm"
                            placeholder="Например: Тинькофф, сбербанк, 044525225…"
                            value={bankSearchQuery}
                            onChange={(e) => {
                              allowBankSuggestFetch.current = true;
                              const next = e.target.value;
                              setBankSearchQuery(next);
                              setBankDraft((d) => ({ ...d, bankName: next }));
                            }}
                            disabled={updateSection.isPending}
                          />
                          {bankSuggestLoading ? (
                            <Text size="xs" c="dimmed">
                              Поиск в DaData…
                            </Text>
                          ) : null}
                          {bankSuggestions.length > 0 ? (
                            <Paper
                              className="seller-profile-bank-suggestions"
                              withBorder
                              radius="sm"
                              styles={{ root: { padding: rem(4) } }}
                            >
                              <ScrollArea.Autosize
                                mah={240}
                                styles={{
                                  viewport: {
                                    paddingInlineEnd: 0,
                                    paddingBottom: 0,
                                  },
                                }}
                              >
                                <Stack gap={0}>
                                  {bankSuggestions.map((opt) => (
                                    <UnstyledButton
                                      key={opt.id}
                                      type="button"
                                      disabled={bankSuggestLoading}
                                      onClick={() => handleBankSuggestionPick(opt)}
                                      style={{
                                        borderRadius: 6,
                                        textAlign: 'left',
                                        width: '100%',
                                        padding: `${rem(6)} ${rem(6)}`,
                                      }}
                                    >
                                      <Text size="sm" lh={1.35}>
                                        {opt.label}
                                      </Text>
                                      <Text size="xs" c="dimmed">
                                        БИК {opt.bik}
                                        {opt.correspondentAccount
                                          ? ` · к/с ${opt.correspondentAccount}`
                                          : ''}
                                      </Text>
                                    </UnstyledButton>
                                  ))}
                                </Stack>
                              </ScrollArea.Autosize>
                            </Paper>
                          ) : null}
                        </Stack>
                      </Box>
                    </div>
                    <Row
                      label="Корр. счёт"
                      unstyledValue
                      value={
                        <TextInput
                          className="seller-profile-inline-input"
                          size="sm"
                          value={bankDraft.correspondentAccount}
                          disabled
                        />
                      }
                    />
                    <Row
                      label="БИК"
                      unstyledValue
                      value={
                        <TextInput
                          className="seller-profile-inline-input"
                          size="sm"
                          value={bankDraft.bik}
                          disabled
                        />
                      }
                    />
                    <Row
                      label="Расчётный счёт"
                      unstyledValue
                      value={
                        <TextInput
                          className="seller-profile-inline-input"
                          size="sm"
                          inputMode="numeric"
                          placeholder="20 цифр"
                          maxLength={20}
                          value={bankDraft.bankAccountNumber}
                          onChange={(e) =>
                            setBankDraft((d) => ({
                              ...d,
                              bankAccountNumber: e.target.value.replace(/\D/g, '').slice(0, 20),
                            }))
                          }
                          disabled={updateSection.isPending}
                        />
                      }
                    />
                  </>
                ) : (
                  <>
                    <Row label="Название банка" value={app.bankName} />
                    <Row label="Корр. счёт" value={app.correspondentAccount} />
                    <Row label="БИК" value={app.bik} />
                    <Row label="Расчётный счёт" value={app.bankAccountNumber} />
                    {!BANK_DETAILS_SELF_EDIT_ENABLED && app.cabinetRole !== 'STAFF' ? (
                      <Text size="xs" c="dimmed" mt="xs">
                        На эти реквизиты приходят выплаты. Чтобы их изменить,{' '}
                        <UnstyledButton
                          type="button"
                          onClick={() => navigate('/support-chat')}
                          style={{ fontSize: 'inherit', textDecoration: 'underline', color: 'inherit' }}
                        >
                          напишите в поддержку
                        </UnstyledButton>
                        {' '}.
                      </Text>
                    ) : null}
                  </>
                )}
              </ProfileCard>
            </Tabs.Panel>

            <Tabs.Panel value="login" className="seller-profile-tab-panel">
              <ProfileCard title="Данные для входа">
                <p className="seller-profile-login-lead">
                  Телефон и email для входа в кабинет продавца.
                </p>
                <LoginCredentialBindRow
                  kind="phone"
                  currentValue={app.user?.phone}
                  canEdit={app.cabinetRole !== 'STAFF'}
                  onBound={() => {
                    void queryClient.invalidateQueries({ queryKey: SELLER_ME_QUERY_KEY });
                  }}
                />
                <LoginCredentialBindRow
                  kind="email"
                  currentValue={app.user?.email}
                  canEdit={app.cabinetRole !== 'STAFF'}
                  onBound={() => {
                    void queryClient.invalidateQueries({ queryKey: SELLER_ME_QUERY_KEY });
                  }}
                />
              </ProfileCard>
            </Tabs.Panel>

            <Tabs.Panel value="documents" className="seller-profile-tab-panel">
              <ProfileCard title="Документы">
                <div className="seller-profile-docs-grid">
                  <DocTilesFromUrls title="Выписка ЕГРЮЛ / ЕГРИП" urls={app.egrulExtractFileUrls} />
                  <DocTilesFromUrls title="Скан паспорта" urls={app.passportScanFileUrls} />
                  <DocTile
                    title="Политика конфиденциальности"
                    href={sellerPrivacyPolicyUrl()}
                  />
                  <DocTile
                    title="Договор-оферта"
                    meta={
                      app.offerPdfFileUrl?.trim()
                        ? [
                          app.offerTemplateVersion != null
                            ? `v${app.offerTemplateVersion}`
                            : null,
                          app.offerAcceptedAt
                            ? `подписан ${formatDate(app.offerAcceptedAt)}`
                            : null,
                        ]
                          .filter(Boolean)
                          .join(' · ') || 'PDF'
                        : null
                    }
                    href={app.offerPdfFileUrl}
                  />
                </div>

                {previousOfferDocuments.length > 0 ? (
                  <div className="seller-profile-docs-history">
                    <UnstyledButton
                      type="button"
                      onClick={() => setOfferHistoryOpen((v) => !v)}
                      className="mantine-focus-never seller-profile-offer-history-toggle"
                    >
                      <Text size="xs">
                        {offerHistoryOpen
                          ? 'Скрыть предыдущие версии оферты'
                          : 'Показать предыдущие версии оферты'}
                      </Text>
                    </UnstyledButton>
                    {offerHistoryOpen ? (
                      <div className="seller-profile-docs-grid seller-profile-docs-grid--history">
                        {previousOfferDocuments.map((doc) => (
                          <DocTile
                            key={doc.id}
                            title={`Оферта v${doc.templateVersion}`}
                            meta={[
                              `обновлена ${formatDate(doc.templateUpdatedAt)}`,
                              doc.acceptedAt
                                ? `подписана ${formatDate(doc.acceptedAt)}`
                                : null,
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                            href={doc.pdfFileUrl}
                          />
                        ))}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </ProfileCard>
            </Tabs.Panel>
          </div>
        </div>
      </Tabs>

      <DoubleCheckModal
        isOpen={hideProfileModalOpen}
        onClose={() => setHideProfileModalOpen(false)}
        title="Скрыть магазин с витрины?"
        message={
          <>
            Покупатели не увидят товары, точки самовывоза и страницу магазина. Кабинет останется
            доступным — магазин можно снова показать в любой момент.
          </>
        }
        secondButtonText="Скрыть с витрины"
        secondButtonAction={() => profileVisibilityMutation.mutate(true)}
        isLoading={profileVisibilityMutation.isPending}
      />

      <DoubleCheckModal
        isOpen={deleteProfileModalOpen}
        onClose={() => setDeleteProfileModalOpen(false)}
        title="Удалить магазин?"
        message={
          <>
            Магазин сразу скроется с витрины. Фактическое удаление произойдёт через 30 дней.
            Отменить заявку в разделе «Мои данные».
          </>
        }
        secondButtonText="Подать заявку на удаление"
        secondButtonAction={() => requestProfileDeletionMutation.mutate()}
        isLoading={requestProfileDeletionMutation.isPending}
      />

      <DoubleCheckModal
        isOpen={cancelProfileChangeField != null}
        onClose={() => setCancelProfileChangeField(null)}
        title={
          cancelProfileChangeField
            ? `Отозвать: ${PROFILE_CHANGE_FIELD_LABELS[cancelProfileChangeField].toLowerCase()}?`
            : 'Отозвать изменение?'
        }
        message={
          <>
            Изменение снимется с модерации. На витрине останется текущее значение — его можно
            изменить и отправить снова в любой момент.
          </>
        }
        firstButtonText="Не отзывать"
        secondButtonText="Отозвать"
        secondButtonAction={() => {
          if (cancelProfileChangeField) cancelProfileChangesMutation.mutate(cancelProfileChangeField);
        }}
        isLoading={cancelProfileChangesMutation.isPending}
      />

      <Modal
        isOpen={sentToModerationLabel != null}
        onClose={() => setSentToModerationLabel(null)}
        title="Изменения отправлены на модерацию"
        maxWidth={420}
      >
        <div className="seller-profile-moderation-sent">
          <ul className="seller-profile-moderation-sent__list">
            <li>
              <strong>{sentToModerationLabel}</strong> появится на витрине после проверки
              модератором.
            </li>
            <li>Пока идёт проверка, покупатели видят прежние данные — магазин работает как обычно.</li>
            <li>О результате придёт уведомление. Если изменения отклонят, вы увидите причину здесь.</li>
          </ul>
          <div className="seller-profile-moderation-sent__actions">
            <Button variant="primary" onClick={() => setSentToModerationLabel(null)}>
              Понятно
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
